import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { Trash2, Shield, User as UserIcon, Calendar, Mail } from 'lucide-react';
import Logo from '../components/Logo';
import ConfirmModal from '../components/ConfirmModal';

const AdminPage: React.FC = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [userToDelete, setUserToDelete] = useState<any | null>(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteUser = async () => {
        if (!userToDelete) return;
        try {
            await api.delete(`/admin/users/${userToDelete.id}`);
            setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('No se pudo eliminar el usuario.');
        } finally {
            setUserToDelete(null);
        }
    };

    if (user?.role !== 'ADMIN') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem' }}>
                <Shield size={64} color="#ef4444" style={{ opacity: 0.5 }} />
                <h1 style={{ fontSize: '1.5rem', color: '#ef4444' }}>Acceso Denegado</h1>
                <p style={{ color: 'var(--text-muted)' }}>No tienes permisos para acceder a esta sección.</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
            <header style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '3rem' }}>
                <Logo size="small" />
                <div style={{ height: '32px', width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                <h1 style={{ fontSize: '1.5rem' }}>Panel de Administración</h1>
            </header>

            <div className="glass-card" style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <UserIcon size={24} /> Gestión de Usuarios
                    </h2>
                    <span style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>
                        {users.length} Usuarios Registrados
                    </span>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>Cargando usuarios...</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                    <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Usuario</th>
                                    <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Rol</th>
                                    <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>ELO / Puzzles</th>
                                    <th style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>Registro</th>
                                    <th style={{ padding: '1rem', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 600 }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '1.25rem 1rem' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600 }}>{u.name || 'Sin nombre'}</span>
                                                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                    <Mail size={12} /> {u.email}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '0.4rem',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                background: u.role === 'ADMIN' ? 'rgba(239, 44, 44, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                                color: u.role === 'ADMIN' ? '#ef4444' : '#3b82f6'
                                            }}>
                                                {u.role === 'ADMIN' ? <Shield size={12} /> : null}
                                                {u.role}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontSize: '0.875rem' }}>
                                                <span style={{ color: '#3b82f6', fontWeight: 600 }}>{u.elo}</span> / <span style={{ color: '#f59e0b', fontWeight: 600 }}>{u.puzzleRating}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <Calendar size={14} /> {new Date(u.createdAt).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                                            <button
                                                onClick={() => setUserToDelete(u)}
                                                disabled={u.id === user?.id}
                                                style={{
                                                    background: 'transparent',
                                                    color: u.id === user?.id ? 'rgba(239, 44, 44, 0.3)' : '#ef4444',
                                                    border: 'none',
                                                    cursor: u.id === user?.id ? 'not-allowed' : 'pointer',
                                                    padding: '0.5rem'
                                                }}
                                                title={u.id === user?.id ? 'No puedes borrarte a ti mismo' : 'Borrar usuario'}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={!!userToDelete}
                title="Eliminar Usuario"
                message={`¿Estás seguro de que deseas eliminar a ${userToDelete?.name || userToDelete?.email}? Esta acción es irreversible.`}
                confirmText="Eliminar"
                cancelText="Cancelar"
                onConfirm={deleteUser}
                onCancel={() => setUserToDelete(null)}
                type="danger"
            />
        </div>
    );
};

export default AdminPage;
